/**
 * <store-detail> — individual store page.
 * Reorders the server-rendered hours table to start from today, then fetches
 * Google Places rating/reviews/photos via the Cloudflare Worker proxy
 * (data-worker-url + data-place-id) — that call needs a server-side key so
 * it can't run purely client-side.
 */

if (!customElements.get('store-detail')) {
  function buildStars(rating) {
    const full = Math.floor(rating);
    const empty = 5 - Math.round(rating);
    return '★'.repeat(full) + (rating % 1 >= 0.5 ? '★' : '') + '☆'.repeat(empty);
  }

  class StoreDetail extends HTMLElement {
    connectedCallback() {
      this.reorderHours();
      this.loadPlaceData();
    }

    reorderHours() {
      const table = this.querySelector('[data-hours-table]');
      if (!table) return;

      const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const todayJs = new Date().getDay();
      const todayIndex = todayJs === 0 ? 6 : todayJs - 1;
      const todayName = allDays[todayIndex];

      const rows = Array.from(table.querySelectorAll('[data-hour-row]'));
      const reordered = rows.slice(todayIndex).concat(rows.slice(0, todayIndex));

      table.innerHTML = '';
      reordered.forEach((row) => {
        row.classList.toggle('store-detail__hour-row--today', row.dataset.day === todayName);
        table.appendChild(row);
      });
    }

    async loadPlaceData() {
      const placeId = this.dataset.placeId;
      const workerUrl = this.dataset.workerUrl;
      if (!placeId || !workerUrl) return;

      let data;
      try {
        const res = await fetch(`${workerUrl}/places/${encodeURIComponent(placeId)}`);
        data = await res.json();
      } catch (e) {
        console.error('Store detail: Places fetch failed', e);
      }

      const ratingEl = this.querySelector('[data-rating]');
      if (!data) {
        if (ratingEl) ratingEl.innerHTML = '';
        return;
      }

      if (ratingEl) {
        ratingEl.innerHTML = data.rating
          ? `<span class="store-detail__stars">${buildStars(data.rating)}</span><span>${data.rating.toFixed(1)} from ${data.userRatingCount || 0} reviews</span>`
          : '<span class="store-detail__rating-empty">No reviews yet</span>';
      }

      this.renderReviews(data.reviews);
      this.renderPhotos(data.photos, workerUrl);
    }

    renderReviews(reviews) {
      const section = this.querySelector('[data-reviews-section]');
      const carousel = this.querySelector('[data-reviews-carousel]');
      if (!section || !carousel || !reviews || !reviews.length) return;

      const max = Math.min(reviews.length, 5);
      let html = '';

      for (let i = 0; i < max; i++) {
        const review = reviews[i];
        const author = review.authorAttribution?.displayName || 'Anonymous';
        const initial = author.charAt(0).toUpperCase();
        const text = review.text?.text || review.text || '';
        const stars = typeof review.rating === 'number' ? buildStars(review.rating) : '';
        const date = review.relativePublishTimeDescription || '';

        html += `
          <div class="store-detail__review-card">
            <div class="store-detail__review-header">
              <div class="store-detail__review-avatar">${initial}</div>
              <div>
                <div class="store-detail__review-author">${author}</div>
                <div class="store-detail__review-date">${date}</div>
              </div>
            </div>
            <div class="store-detail__review-stars">${stars}</div>
            <div class="store-detail__review-text">${text}</div>
          </div>
        `;
      }

      carousel.innerHTML = html;
      section.hidden = false;
    }

    async renderPhotos(photos, workerUrl) {
      const section = this.querySelector('[data-photos-section]');
      const grid = this.querySelector('[data-photos-grid]');
      if (!section || !grid || !photos || !photos.length) return;

      const max = Math.min(photos.length, 6);
      const alt = this.dataset.storeName ? `${this.dataset.storeName} store photo` : 'Store photo';

      const resolved = await Promise.all(
        photos.slice(0, max).map(async (photo) => {
          let url = photo.mediaUrl;
          if (!url && photo.name) {
            try {
              const res = await fetch(`${workerUrl}/photo/${encodeURIComponent(photo.name)}`);
              const data = await res.json();
              url = data?.url || null;
            } catch (e) {
              url = null;
            }
          }
          return { url, href: photo.googleMapsUri || null };
        })
      );

      let html = '';
      resolved.forEach((entry) => {
        if (!entry.url) return;
        html += '<div class="store-detail__photo">';
        if (entry.href) html += `<a href="${entry.href}" target="_blank" rel="noopener">`;
        html += `<img src="${entry.url}" alt="${alt}" loading="lazy">`;
        if (entry.href) html += '</a>';
        html += '</div>';
      });

      if (html) {
        grid.innerHTML = html;
        section.hidden = false;
      }
    }
  }

  customElements.define('store-detail', StoreDetail);
}
